import { makeStyles, Snackbar, withStyles } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { useIO, useLinked, useModel } from "@type-r/react";
import { RowH1 } from "common";
import { CreateWorkflow, div, Flex, Loading, navigate, Page, WorkflowProps } from "lqx-react";
import * as React from "react";
import { ValidationRuleDetails } from "../../api/validationRuleDetails";
import { ValidationRuleDefineStep } from "./validation-rules-define-step";
import { ValidationRuleDetailsStep } from "./validation-rules-details-step";
import { ValidationRuleDuplicationCheckStep } from "./validation-rules-duplication-ckeck-step";
import { validation } from "../../api/validationRuleValidation";

export const ValidationRulesWizardPage = ({ id }: { id?: string }) => {
  const isEdit = id ? true : false;
  const validationRuleDetails = useModel(ValidationRuleDetails);
  const classes = useStyles();
  const $workflowStep = useLinked(0);
  const $doStartOver = useLinked(false);
  const $showErrors = useLinked(false);
  const $errorMessage = useLinked(["Default Error!"]);
  const $showSuccessfulEditMessage = useLinked(false);
  const doStartOver = () => $doStartOver.set(true);
  //const goToNextStep = () => $workflowStep.set($workflowStep.value + 1);
  const goToNextStep = async (inputDetails) => {
      if ($workflowStep.value === 0) {
        const validationResult = await validation(inputDetails.id, inputDetails.name, inputDetails.assetTypeId);
        if (!validationResult.isValid) {
          $showErrors.set(true);
          $errorMessage.set(validationResult.errors);
          return;
        }
      }
      $workflowStep.set($workflowStep.value + 1);
    };
  const goToPreviousStep = () => $workflowStep.set($workflowStep.value - 1);
  const cancel = () => navigate('validation-rules');;
  const isDetailsStepEnabled = () => $workflowStep.value === 0;
  const isValidationStepEnabled = () => $workflowStep.value === 1;
  const isSaveStepEnabled = () => $workflowStep.value === 2;

  const isReady = useIO(
    async () => {
      isEdit ? await validationRuleDetails.get(id).then() : null;
    }
  );

  const handleCloseShowErrors = (_, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    $showErrors.set(false);
  };

  const handleCloseShowSuccessfulEditMessage = (_, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    $showSuccessfulEditMessage.set(false);
  };

  const isStartOverReady = useIO(
    async () => $doStartOver.isTruthy && startOver(),
    [$doStartOver.value]
  );

  const startOver = () => {
    try {
      if (!isEdit) {
        validationRuleDetails.set(validationRuleDetails.defaults());
      }
      $workflowStep.set(0);
    }
    finally {
      $doStartOver.set(false);
    }
  };

  const getTitle = () => {
    if (isEdit) return 'Edit Validation Sets';
    return 'Validation Sets';
  }

  const showErrors = () =>
    $showErrors.value && $errorMessage.value && $errorMessage.value.length > 0

  return (isStartOverReady && isReady ?
    <Page maxWidth={false} header={
      <Flex center>
        <RowH1 className={classes.title}>{getTitle()}</RowH1>
      </Flex>
    }>
      <Flex row>
        <IngestWorkflowContainer>
          <IngestWorkflow activeStep={$workflowStep.value} steps={!isEdit ? workflowStepsForCreate : workflowStepsForEdit} />
        </IngestWorkflowContainer>
      </Flex>
      {
        isDetailsStepEnabled() &&
        <ValidationRuleDetailsStep
          next={goToNextStep}
          cancel={cancel}
          startOver={doStartOver}
          validationRuleDetails={validationRuleDetails}
        />
      }
      {
        isValidationStepEnabled() &&
        <ValidationRuleDefineStep
          next={goToNextStep}
          back={goToPreviousStep}
          cancel={cancel}
          startOver={doStartOver}
          isEdit={isEdit}
          validationRuleDetails={validationRuleDetails} />
      }
      {
        isSaveStepEnabled() &&
        <ValidationRuleDuplicationCheckStep
          back={goToPreviousStep}
          cancel={cancel}
          startOver={doStartOver}
          validationRuleDetails={validationRuleDetails}
        />
      }
      <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={$showErrors.value} autoHideDuration={6000} onClose={handleCloseShowErrors}>
        <div>
          {showErrors() && $errorMessage.value.map((error) =>
            <StyledAlert severity="error">
              {error}
            </StyledAlert>
          )}</div>
      </Snackbar>
      <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={$showSuccessfulEditMessage.value} autoHideDuration={6000} onClose={handleCloseShowSuccessfulEditMessage}>
        <Alert severity="success">
          Validation successfully updated
        </Alert>
      </Snackbar>
    </Page> : <Loading />
  );
};

const StyledAlert = withStyles({
  root: {
    margin: '8px',
    width: '60vw',
    border: '1px solid lightgray'
  }
})(Alert)

const useStyles = makeStyles({
  title: {
    color: '#3d6889'
  }
});

const IngestWorkflowContainer = div({
  maxWidth: '1000px',
  width: '100%',
  marginLeft: 'auto',
  marginRight: 'auto'
});

const IngestWorkflow = ({ activeStep, steps }: Partial<WorkflowProps>) => CreateWorkflow({
  activeStep: activeStep,
  steps: steps
});

const workflowStepsForCreate = [
  {
    title: 'NAME YOUR VALIDATION SETS AND FILL OUT DETAILS',
    description: '',
  },
  {
    title: 'SELECT VALIDATION FIELDS',
    description: 'Define which fields require validation',
  },
  // {
  //   title: 'VIEW PARSE RESULTS, MAP FIELDS AND SAVE VALIDATION SETS',
  //   description: 'Map results to supported fields',
  // }
];

const workflowStepsForEdit = [
  {
    title: 'NAME YOUR VALIDATION SETS AND FILL OUT DETAILS',
    description: '',
  },
  {
    title: 'SELECT VALIDATION FIELDS',
    description: 'Define which fields require validation',
  }
];